import type { AdEnginePlugin } from "./core/AdEnginePlugin";

import { debug } from "./debug";
import { getInstantConfig } from "./helpers/instantConfig";
import { initAdEngine } from "./core/initAdEngine";

import { A9Plugin } from "./plugins/A9Plugin/A9Plugin";
import { AssertiveYieldPlugin } from "./plugins/AssertiveYieldPlugin/AssertiveYieldPlugin";
import { ConsentManagementPrebidPlugin } from "./plugins/PrebidPlugin/ConsentManagementPrebidPlugin";
import { DigitalServicesActPlugin } from "./plugins/DigitalServicesActPlugin/DigitalServicesActPlugin";
import { DynamicSlotPlugin } from "./plugins/DynamicSlotPlugin/DynamicSlotPlugin";
import { FandomTaxonomyPlugin } from "./plugins/FandomTaxonomyPlugin/FandomTaxonomyPlugin";
import { FloorAdhesionPlugin } from "./plugins/FloorAdhesionPlugin/FloorAdhesionPlugin";
import { LiveConnectPlugin } from "./plugins/LiveConnectPlugin/LiveConnectPlugin";
import { OpenRtbPlugin } from "./plugins/OpenRtbPlugin/OpenRtbPlugin";
import { PageLevelTargetingPlugin } from "./plugins/PageLevelTargetingPlugin/PageLevelTargetingPlugin";
import { PerformanceTestPlugin } from "./plugins/PerformanceTestPlugin/PerformanceTestPlugin";
import { PrebidPlugin } from "./plugins/PrebidPlugin/PrebidPlugin";
import { RepeatableSlotsPlugin } from "./plugins/RepeatableSlotsPlugin/RepeatableSlotsPlugin";
import { SlotRefresherPlugin } from "./plugins/SlotRefresherPlugin/SlotRefresherPlugin";
import { ServerToServerPlugin } from "./plugins/PrebidPlugin/ServerToServerPlugin";
import { TopStrategyTunerPlugin } from "./plugins/TopStrategyTunerPlugin/TopStrategyTunerPlugin";
import { TrackingPlugin } from "./plugins/TrackingPlugin/TrackingPlugin";
import { LightboxLeaderboardPlugin } from "./plugins/LightboxLeaderboardPlugin/LightboxLeaderboardPlugin";
import { LotamePlugin } from "./plugins/Vendors/Identity/LotamePlugin/LotamePlugin";
import { ConfiantPlugin } from "./plugins/Vendors/BrandSafety/ConfiantPlugin/ConfiantPlugin";
import { IntentIqPlugin } from "./plugins/IntentIqPlugin/IntentIqPlugin";
import { CaptifyPlugin } from "./plugins/Vendors/Identity/CaptifyPlugin/CaptifyPlugin";
import { VastUrlPlugin } from "./plugins/VastUrlPlugin/VastUrlPlugin";
import { BrandMetricsPlugin } from "./plugins/Vendors/Identity/BrandMetricsPlugin/BrandMetricsPlugin";
import { AudigentPlugin } from "./plugins/Vendors/Identity/AudigentPlugin/AudigentPlugin";
import { DoubleVerifyPlugin } from "./plugins/Vendors/BrandSafety/DoubleVerify/DoubleVerifyPlugin";
import { InterstitialPlugin } from "./plugins/InterstitialPlugin/InterstitialPlugin";
import { JWPlayerRTDPrebidPlugin } from "./plugins/PrebidPlugin/JWPlayerRTDPrebidPlugin";
import { IasPublisherOptimizationPlugin } from "./plugins/Vendors/BrandSafety/IasPublisherOptimizationPlugin/IasPublisherOptimizationPlugin";
import { InContentPlayerPlugin } from "./plugins/InContentPlayerPlugin/InContentPlayerPlugin";
import {
  LabradorEvent,
  type LabradorTrackData,
  PerformanceMarkName,
} from "common-types";
import { NielsenPlugin } from "./plugins/Vendors/Identity/NielsenPlugin/NielsenPlugin";
import { BlockthroughReadershipLinkPlugin } from "./plugins/Vendors/AdRecovery/BlockthroughReadershipLinkPlugin/BlockthroughReadershipLinkPlugin";
import { AdLabelPlugin } from "./plugins/AdLabelPlugin/AdLabelPlugin";
import { pushTrack } from "utils";
import { PrebidIdSyncingPlugin } from "./plugins/PrebidPlugin/PrebidIdSyncingPlugin/PrebidIdSyncingPlugin";
import type { InstantConfigRepository } from "instant-config";
import type { AdEngine } from "./core/AdEngine";
import { TlbPlaceholderPlugin } from "./plugins/TlbPlaceholderPlugin/TlbPlaceholderPlugin";

export type ExposedPlugins = Awaited<ReturnType<typeof init>>;
type AdEnginePluginConstructor = {
  new (config: InstantConfigRepository): AdEnginePlugin;
  isEnabled(config: InstantConfigRepository): boolean;
};

function initializePlugins(
  plugins: AdEnginePluginConstructor[],
  config: InstantConfigRepository,
): AdEnginePlugin[] {
  return plugins
    .filter((Plugin) => Plugin.isEnabled(config))
    .map((Plugin) => new Plugin(config));
}

/**
 * Initializes all third party vendor plugins such as identity, brand safety, and non prebid bidders
 *
 * @param instantConfig The configuration values for the plugins
 * @returns The array of initialized plugins
 */
function initVendorPlugins(
  instantConfig: InstantConfigRepository,
): AdEnginePlugin[] {
  return initializePlugins(
    [
      A9Plugin,
      AssertiveYieldPlugin,
      AudigentPlugin,
      BrandMetricsPlugin,
      CaptifyPlugin,
      ConfiantPlugin,
      DoubleVerifyPlugin,
      IasPublisherOptimizationPlugin,
      IntentIqPlugin,
      LiveConnectPlugin,
      LotamePlugin,
      NielsenPlugin,
      BlockthroughReadershipLinkPlugin,
    ],
    instantConfig,
  );
}

/**
 * Initializes all prebid specific plugins
 *
 * @param instantConfig The configuration values for the plugins
 * @returns The array of initialized plugins
 */
function initPrebidPlugins(
  instantConfig: InstantConfigRepository,
): AdEnginePlugin[] {
  return initializePlugins(
    [
      ConsentManagementPrebidPlugin,
      PrebidPlugin,
      ServerToServerPlugin,
      PrebidIdSyncingPlugin,
      JWPlayerRTDPrebidPlugin,
    ],
    instantConfig,
  );
}

export async function init() {
  const { config: instantConfig, activeLabradorKeyValues } =
    await getInstantConfig();

  /**
   * Exposed plugins that can be used from outside the ad engine
   */
  const exposedPlugins = {
    InterstitialPlugin: new InterstitialPlugin(),
    // it could be used like this: window.fandomAds.getPlugin("ExampleExposedPlugin").exampleExposedMethod();
    FloorAdhesionPlugin: new FloorAdhesionPlugin(),
    TopStrategyTunerPlugin: new TopStrategyTunerPlugin(instantConfig),
    SlotRefresherPlugin: new SlotRefresherPlugin(instantConfig),
    VastUrlPlugin: new VastUrlPlugin(instantConfig),
    LightboxLeaderboardPlugin: new LightboxLeaderboardPlugin(),
    InContentPlayerPlugin: new InContentPlayerPlugin(),
  };

  const trackingPlugin = new TrackingPlugin();
  const allPlugins: AdEnginePlugin[] = [
    trackingPlugin,
    new DynamicSlotPlugin(),
    ...Object.values(exposedPlugins),
    new FandomTaxonomyPlugin(),
    new PageLevelTargetingPlugin(activeLabradorKeyValues),
    new PerformanceTestPlugin(),
    new AdLabelPlugin(),
    new RepeatableSlotsPlugin(),
    new DigitalServicesActPlugin(),
    new OpenRtbPlugin(),
    new TlbPlaceholderPlugin(),
    ...initPrebidPlugins(instantConfig),
    ...initVendorPlugins(instantConfig),
    ...(await debug()),
  ];
  const pluginsMap = new Map<AdEnginePluginConstructor, AdEnginePlugin>(
    allPlugins.map((plugin) => [
      plugin.constructor as AdEnginePluginConstructor,
      plugin,
    ]),
  );

  allPlugins.forEach((plugin) => {
    plugin.setPlugins(pluginsMap);
  });
  performance.mark(PerformanceMarkName.AE_AFTER_INIT_PLUGINS);

  const labradorTrackPayload: LabradorTrackData["payload"] = {
    flags: activeLabradorKeyValues,
  };
  pushTrack(LabradorEvent.LABRADOR_KEYVALUES, labradorTrackPayload);

  const adEngine: AdEngine = await initAdEngine(instantConfig, allPlugins);
  adEngine.start();

  return exposedPlugins;
}
